// Jun 2026 (Randy) — Live theme word editor.
// Opens from the ACTIVE theme row in the Theme Archive (only access point).
// Lists every manifest label grouped by app area; any word can be hand-edited
// and re-pushed. Includes search + a case-aware batch find-and-replace
// ("change all drill → practice"). Saves via the non-destructive
// /calendar-theme/words merge endpoint — no wipe, no history entry.
import { useEffect, useMemo, useState } from "react";
import { X, MagnifyingGlass, ArrowCounterClockwise, Spinner, Swap, FloppyDisk, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { THEME_MANIFEST } from "@/lib/themeManifest";
import { useCalendarTheme } from "@/lib/CalendarThemeProvider";

const CATEGORY_LABELS = {
  calendar: "Calendar", event: "Events", chores: "Chores library",
  household: "Household / Team setup", kidmodal: "Per-person modals",
  smartchores: "AI plan creator", rewards: "Reward store",
  master_dashboard: "Wall dashboard", familyview: "Member view",
  kid: "Kid / member home", digest: "Digest", fanportal: "Fan portal",
  notifications: "Notifications", archive: "Theme archive",
};

function matchCase(sample, word) {
  if (!word) return word;
  if (sample === sample.toUpperCase() && /[A-Z]/.test(sample)) return word.toUpperCase();
  if (sample[0] === sample[0].toUpperCase()) return word[0].toUpperCase() + word.slice(1);
  return word.toLowerCase();
}

function smartReplace(text, from, to) {
  const esc = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(new RegExp(esc, "gi"), (m) => matchCase(m, to));
}

export default function ThemeWordEditorModal({ siloId, onClose }) {
  const { reload } = useCalendarTheme();
  const [savedMap, setSavedMap] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [query, setQuery] = useState("");
  const [batchFrom, setBatchFrom] = useState("");
  const [batchTo, setBatchTo] = useState("");
  const [batchNote, setBatchNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Feb 2026 (Randy pt-theme-rename) — theme rename lives inside this
  // modal (item E). Saved value comes from theme_meta.name; the local
  // draft flows through the same "unsaved changes" pipeline as words.
  const [savedName, setSavedName] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/calendar/silos/${siloId}/calendar-theme`);
        if (alive) {
          setSavedMap(r.data?.theme_map || {});
          const nm = (r.data?.theme_meta?.name || "").trim();
          setSavedName(nm);
          setNameDraft(nm);
        }
      } catch {
        if (alive) { toast.error("Couldn't load the active theme."); onClose(); }
      }
    })();
    return () => { alive = false; };
  }, [siloId, onClose]);

  const entries = useMemo(() =>
    Object.entries(THEME_MANIFEST).map(([key, e]) => ({
      key, default: e.defaultLabel, budget: e.charBudget || 40, category: e.category || "other",
    })), []);

  const effective = (key, def) => {
    if (drafts[key] !== undefined) return drafts[key];
    return savedMap?.[key] ?? "";
  };

  const changedKeys = useMemo(() => {
    if (!savedMap) return [];
    return Object.keys(drafts).filter((k) => drafts[k] !== (savedMap[k] ?? ""));
  }, [drafts, savedMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const cur = effective(e.key, e.default) || e.default;
      return e.default.toLowerCase().includes(q) || cur.toLowerCase().includes(q) || e.key.toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, query, drafts, savedMap]);

  const grouped = useMemo(() => {
    const out = [];
    const byCat = {};
    for (const e of filtered) {
      if (!byCat[e.category]) { byCat[e.category] = []; out.push(e.category); }
      byCat[e.category].push(e);
    }
    return out.map((c) => [c, byCat[c]]);
  }, [filtered]);

  const runBatch = () => {
    const from = batchFrom.trim();
    const to = batchTo.trim();
    if (!from || !to) { setBatchNote("Type both words first."); return; }
    let hits = 0;
    const next = { ...drafts };
    for (const e of entries) {
      const base = (drafts[e.key] !== undefined ? drafts[e.key] : (savedMap?.[e.key] ?? "")) || e.default;
      if (!base.toLowerCase().includes(from.toLowerCase())) continue;
      const replaced = smartReplace(base, from, to).slice(0, e.budget);
      if (replaced !== (savedMap?.[e.key] ?? "") || drafts[e.key] !== undefined) next[e.key] = replaced;
      hits += 1;
    }
    setDrafts(next);
    if (hits && query.trim() && query.toLowerCase().includes(from.toLowerCase())) setQuery(to);
    setBatchNote(hits ? `Changed ${hits} label${hits === 1 ? "" : "s"} — review below, then Save.` : `No labels contain “${from}”.`);
  };

  const save = async () => {
    if (!changedKeys.length) return;
    setSaving(true);
    try {
      const updates = {};
      for (const k of changedKeys) updates[k] = drafts[k];
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/words`, { updates });
      setSavedMap(r.data?.theme_map || savedMap);
      setDrafts({});
      setBatchNote("");
      toast.success(`Updated ${(r.data?.changed ?? 0) + (r.data?.cleared ?? 0)} words — live everywhere now.`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the word changes.");
    } finally { setSaving(false); }
  };

  // Feb 2026 (Randy pt-theme-rename) — save the rename via the
  // dedicated /calendar-theme/current/name endpoint (no LLM, no
  // history entry). Empty string clears back to blank so the archive
  // row falls back to the source_prompt / "Untitled" label.
  const nameDirty = (nameDraft || "").trim() !== (savedName || "").trim();
  const saveName = async () => {
    if (!nameDirty) return;
    setSavingName(true);
    try {
      const trimmed = (nameDraft || "").trim();
      const r = await api.post(`/calendar/silos/${siloId}/calendar-theme/current/name`, { name: trimmed });
      const nm = (r.data?.theme_meta?.name || "").trim();
      setSavedName(nm);
      setNameDraft(nm);
      toast.success(trimmed ? `Theme renamed to “${trimmed}”.` : "Theme name cleared.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rename the theme.");
    } finally { setSavingName(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/45 flex items-center justify-center p-3"
      data-testid="theme-word-editor-modal"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
        paddingLeft: "max(0.75rem, env(safe-area-inset-left))",
        paddingRight: "max(0.75rem, env(safe-area-inset-right))",
      }}
    >
      <div className="bg-[#FDFCF8] w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b hairline">
          <div>
            <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2">
              <PencilSimple size={16} weight="duotone" /> Edit theme words
            </h2>
            <p className="text-[10px] text-[#6B6A68] mt-0.5">
              Hand-fix any label on the active theme. Empty a box to fall back to the default word. Saving never touches your calendar or content.
            </p>
          </div>
          <button onClick={onClose} data-testid="theme-word-editor-close" className="text-[#6B6A68] hover:text-[#1C1B1A] p-1"><X size={18} /></button>
        </div>

        {/* Item E — rename input pinned at the top of the modal. Uses the
            existing POST /calendar-theme/current/name endpoint (no LLM,
            no history row). Blank clears the pinned name so the archive
            row falls back to source_prompt / "Untitled". */}
        <div className="px-5 pt-3 pb-2 border-b hairline bg-white/60">
          <label htmlFor="theme-rename-input" className="text-[9px] uppercase tracking-[0.14em] text-[#6B6A68] block mb-1">Theme name</label>
          <div className="flex items-center gap-2">
            <input
              id="theme-rename-input"
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value.slice(0, 60))}
              onKeyDown={(e) => { if (e.key === "Enter" && nameDirty && !savingName) saveName(); }}
              placeholder={savedName ? savedName : "e.g. Restore coach, Space cadets, Farm crew…"}
              data-testid="theme-rename-input"
              className="flex-1 text-xs border hairline rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <button
              onClick={saveName}
              disabled={!nameDirty || savingName}
              data-testid="theme-rename-save"
              className="text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1"
            >
              {savingName ? <Spinner size={11} className="animate-spin" /> : <FloppyDisk size={11} weight="duotone" />}
              Save name
            </button>
          </div>
          <p className="text-[10px] text-[#6B6A68] mt-1">
            Pins a label onto this silo&apos;s active theme (visible in the archive row). Leave blank to clear.
          </p>
        </div>

        <div className="px-5 py-3 border-b hairline space-y-2 bg-white/60">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8A88]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="theme-word-search"
              placeholder="Search any word — e.g. drill"
              className="w-full text-xs border hairline rounded-md pl-8 pr-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] flex items-center gap-1"><Swap size={12} /> Change all</span>
            <input value={batchFrom} onChange={(e) => setBatchFrom(e.target.value)} data-testid="theme-word-batch-from"
              placeholder="drill" className="w-28 text-xs border hairline rounded-md px-2 py-1.5 bg-white" />
            <span className="text-[#8A8A88] text-xs">→</span>
            <input value={batchTo} onChange={(e) => setBatchTo(e.target.value)} data-testid="theme-word-batch-to"
              placeholder="practice" onKeyDown={(e) => { if (e.key === "Enter") runBatch(); }}
              className="w-28 text-xs border hairline rounded-md px-2 py-1.5 bg-white" />
            <button onClick={runBatch} data-testid="theme-word-batch-apply"
              className="text-[10px] uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">
              Apply
            </button>
            {batchNote && <span className="text-[10px] text-[#6B4E1E]" data-testid="theme-word-batch-note">{batchNote}</span>}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!savedMap ? (
            <div className="flex items-center gap-2 text-xs text-[#6B6A68] py-8 justify-center"><Spinner size={14} className="animate-spin" /> Loading theme…</div>
          ) : grouped.length === 0 ? (
            <div className="text-xs text-[#6B6A68] italic py-8 text-center">No labels match “{query}”.</div>
          ) : grouped.map(([cat, rows]) => (
            <div key={cat} className="mb-4">
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8A88] mb-1.5">{CATEGORY_LABELS[cat] || cat}</div>
              <div className="space-y-1">
                {rows.map((e) => {
                  const saved = savedMap[e.key] ?? "";
                  const val = drafts[e.key] !== undefined ? drafts[e.key] : saved;
                  const dirty = drafts[e.key] !== undefined && drafts[e.key] !== saved;
                  const themed = (dirty ? val : saved) !== "";
                  return (
                    <div key={e.key} data-testid={`theme-word-row-${e.key}`}
                      className={`grid grid-cols-[1fr_auto_1.2fr_auto] items-center gap-2 px-2.5 py-1.5 rounded border ${dirty ? "bg-[#F8F1E2] border-[#EAD9AE]" : themed ? "bg-[#F4F8F5] border-[#D8E4DA]" : "bg-white hairline"}`}>
                      <div className="text-[11px] text-[#6B6A68] truncate" title={e.key}>{e.default}</div>
                      <div className="text-[#8A8A88] text-[10px]">→</div>
                      <input value={val} maxLength={e.budget}
                        onChange={(ev) => setDrafts((d) => ({ ...d, [e.key]: ev.target.value }))}
                        placeholder={e.default} data-testid={`theme-word-input-${e.key}`}
                        className="text-[11px] border hairline rounded px-2 py-1 bg-white w-full focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                      <button onClick={() => setDrafts((d) => { const n = { ...d }; delete n[e.key]; return n; })}
                        disabled={!dirty} title="Undo this edit" data-testid={`theme-word-revert-${e.key}`}
                        className={`p-1 rounded ${dirty ? "text-[#6B4E1E] hover:bg-[#F1E4C8]" : "text-transparent pointer-events-none"}`}>
                        <ArrowCounterClockwise size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t hairline bg-white/70">
          <span className="text-[10px] text-[#6B6A68]" data-testid="theme-word-unsaved-count">
            {changedKeys.length ? `${changedKeys.length} unsaved change${changedKeys.length === 1 ? "" : "s"}` : "No unsaved changes"}
          </span>
          <button onClick={save} disabled={saving || !changedKeys.length} data-testid="theme-word-save"
            className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Spinner size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="duotone" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
